/**
 * External Organizations API service for managing external org collaboration.
 * @module lib/api/external-orgs.api
 */

import {
  AcceptExternalOrgInviteDTO,
  AcceptExternalOrgSupportRequestDTO,
  CreateExternalOrgInviteDTO,
  CreateExternalOrgSupportRequestDTO,
  ExternalOrgInvite,
  ExternalOrgMember,
  ExternalOrgRelationship,
  GrantExternalMemberAccessDTO,
  PaginatedResponse,
  RevokeExternalMemberAccessDTO,
  UpdateExternalOrgRelationshipDTO,
} from '@scrambl/shared'

import { BaseApi } from './base-api'
import { createSingletonApi } from './singleton-factory'

// Extended params type
interface ExternalOrgParams {
  status?: string
  skip?: number
  take?: number
  [key: string]: string | number | boolean | undefined
}

// Response types
interface ApiResponse<T> {
  data: T
  meta?: {
    total?: number
    granted?: number
    revoked?: number
  }
}

interface AcceptInviteResult {
  invite: ExternalOrgInvite
  relationship: ExternalOrgRelationship
}

interface InviteTokenData {
  hostOrganizationId: string
  hostOrganizationName?: string
  hostOrganizationLogo?: string
  partnerEmail: string
  partnerOrgName?: string
  status: string
  message?: string
  expiresAt: string
}

interface SupportRequestTokenData {
  partnerOrganizationId: string
  partnerOrganizationName?: string
  partnerOrganizationLogo?: string
  recipientEmail: string
  hostOrgName?: string
  status: string
  message?: string
  expiresAt: string
}

class ExternalOrgApiService extends BaseApi<ExternalOrgRelationship> {
  constructor() {
    super({
      baseEndpoint: '/external-orgs',
      responseNormalization: true,
    })
  }

  // =====================
  // Assignable Members
  // =====================

  /**
   * Get all external members that can be assigned to tasks/projects in this organization.
   * Returns members from partner orgs where showInAssignees is enabled.
   */
  async getAssignableExternalMembers(options?: {
    signal?: AbortSignal
  }): Promise<ExternalOrgMember[]> {
    const response = await this.makeRequest<ApiResponse<ExternalOrgMember[]>>(
      `${this.config.baseEndpoint}/assignable-members`,
      options,
    )
    return response.data
  }

  // =====================
  // Invite Management (Host Org - Sending)
  // =====================

  /**
   * Send an invite to an external organization
   */
  async sendInvite(
    data: CreateExternalOrgInviteDTO,
  ): Promise<ExternalOrgInvite> {
    const response = await this.makeRequest<ApiResponse<ExternalOrgInvite>>(
      `${this.config.baseEndpoint}/invite`,
      {
        method: 'POST',
        body: data,
      },
    )
    return response.data
  }

  /**
   * Get all sent invites (as host org)
   */
  async getSentInvites(
    params?: ExternalOrgParams,
  ): Promise<ExternalOrgInvite[]> {
    const queryString = this.buildQueryParams(params ?? {})
    const response = await this.makeRequest<ApiResponse<ExternalOrgInvite[]>>(
      `${this.config.baseEndpoint}/invites/sent${queryString}`,
    )
    return response.data
  }

  /**
   * Cancel a pending invite
   */
  async cancelInvite(inviteId: string): Promise<ExternalOrgInvite> {
    const response = await this.makeRequest<ApiResponse<ExternalOrgInvite>>(
      `${this.config.baseEndpoint}/invites/${inviteId}/cancel`,
      { method: 'POST' },
    )
    return response.data
  }

  /**
   * Resend an invite
   */
  async resendInvite(inviteId: string): Promise<ExternalOrgInvite> {
    const response = await this.makeRequest<ApiResponse<ExternalOrgInvite>>(
      `${this.config.baseEndpoint}/invites/${inviteId}/resend`,
      { method: 'POST' },
    )
    return response.data
  }

  // =====================
  // Invite Management (Partner Org - Receiving)
  // =====================

  /**
   * Get all received invites (as partner org)
   */
  async getReceivedInvites(): Promise<ExternalOrgInvite[]> {
    const response = await this.makeRequest<ApiResponse<ExternalOrgInvite[]>>(
      `${this.config.baseEndpoint}/invites/received`,
    )
    return response.data
  }

  /**
   * Get invite details by token (public)
   */
  async getInviteByToken(token: string): Promise<InviteTokenData> {
    return this.makeRequest<InviteTokenData>(
      `${this.config.baseEndpoint}/invites/token/${token}`,
    )
  }

  /**
   * Accept an invite
   */
  async acceptInvite(
    data: AcceptExternalOrgInviteDTO,
  ): Promise<AcceptInviteResult> {
    const response = await this.makeRequest<ApiResponse<AcceptInviteResult>>(
      `${this.config.baseEndpoint}/invites/accept`,
      {
        method: 'POST',
        body: data,
      },
    )
    return response.data
  }

  /**
   * Decline an invite
   */
  async declineInvite(token: string): Promise<ExternalOrgInvite> {
    const response = await this.makeRequest<ApiResponse<ExternalOrgInvite>>(
      `${this.config.baseEndpoint}/invites/decline`,
      {
        method: 'POST',
        body: { token },
      },
    )
    return response.data
  }

  // =====================
  // Relationship Management (Host Org - Managing Partners)
  // =====================

  /**
   * Get all partner organizations (orgs that have access to us as host)
   */
  async getPartnerOrgs(
    params?: ExternalOrgParams,
  ): Promise<ExternalOrgRelationship[]> {
    const queryString = this.buildQueryParams(params ?? {})
    const response = await this.makeRequest<
      ApiResponse<ExternalOrgRelationship[]>
    >(`${this.config.baseEndpoint}/partners${queryString}`)
    return response.data
  }

  /**
   * Get a specific partner relationship
   */
  async getPartnerById(
    relationshipId: string,
  ): Promise<ExternalOrgRelationship> {
    const response = await this.makeRequest<
      ApiResponse<ExternalOrgRelationship>
    >(`${this.config.baseEndpoint}/partners/${relationshipId}`)
    return response.data
  }

  /**
   * Update partner relationship (permissions, showInAssignees)
   */
  async updatePartner(
    relationshipId: string,
    data: UpdateExternalOrgRelationshipDTO,
  ): Promise<ExternalOrgRelationship> {
    const response = await this.makeRequest<
      ApiResponse<ExternalOrgRelationship>
    >(`${this.config.baseEndpoint}/partners/${relationshipId}`, {
      method: 'PATCH',
      body: data,
    })
    return response.data
  }

  /**
   * Suspend partner access
   */
  async suspendPartner(
    relationshipId: string,
  ): Promise<ExternalOrgRelationship> {
    const response = await this.makeRequest<
      ApiResponse<ExternalOrgRelationship>
    >(`${this.config.baseEndpoint}/partners/${relationshipId}/suspend`, {
      method: 'POST',
    })
    return response.data
  }

  /**
   * Reactivate suspended partner access
   */
  async reactivatePartner(
    relationshipId: string,
  ): Promise<ExternalOrgRelationship> {
    const response = await this.makeRequest<
      ApiResponse<ExternalOrgRelationship>
    >(`${this.config.baseEndpoint}/partners/${relationshipId}/reactivate`, {
      method: 'POST',
    })
    return response.data
  }

  /**
   * Revoke partner access permanently
   */
  async revokePartner(
    relationshipId: string,
  ): Promise<ExternalOrgRelationship> {
    const response = await this.makeRequest<
      ApiResponse<ExternalOrgRelationship>
    >(`${this.config.baseEndpoint}/partners/${relationshipId}/revoke`, {
      method: 'POST',
    })
    return response.data
  }

  /**
   * Get external members for a partner relationship
   */
  async getPartnerMembers(
    relationshipId: string,
    params?: ExternalOrgParams,
  ): Promise<ExternalOrgMember[]> {
    const queryString = this.buildQueryParams(params ?? {})
    const response = await this.makeRequest<ApiResponse<ExternalOrgMember[]>>(
      `${this.config.baseEndpoint}/partners/${relationshipId}/members${queryString}`,
    )
    return response.data
  }

  // =====================
  // Relationship Management (Partner Org - Managing Host Access)
  // =====================

  /**
   * Get all host organizations (orgs that we support as partner)
   */
  async getHostOrgs(
    params?: ExternalOrgParams,
  ): Promise<ExternalOrgRelationship[]> {
    const queryString = this.buildQueryParams(params ?? {})
    const response = await this.makeRequest<
      ApiResponse<ExternalOrgRelationship[]>
    >(`${this.config.baseEndpoint}/host-orgs${queryString}`)
    return response.data
  }

  /**
   * Get all host organizations that the current user has access to
   * through external org memberships. This endpoint does NOT require
   * an organization context.
   */
  async getMyHostOrgs(): Promise<ExternalOrgRelationship[]> {
    const response = await this.makeRequest<
      ApiResponse<ExternalOrgRelationship[]>
    >(`${this.config.baseEndpoint}/my-host-orgs`)
    return response.data
  }

  /**
   * Get members with access to a host org
   */
  async getHostOrgMembers(
    relationshipId: string,
    params?: ExternalOrgParams,
  ): Promise<ExternalOrgMember[]> {
    const queryString = this.buildQueryParams(params ?? {})
    const response = await this.makeRequest<ApiResponse<ExternalOrgMember[]>>(
      `${this.config.baseEndpoint}/host-orgs/${relationshipId}/members${queryString}`,
    )
    return response.data
  }

  /**
   * Grant member access to a host org
   */
  async grantMemberAccess(
    relationshipId: string,
    data: GrantExternalMemberAccessDTO,
  ): Promise<ExternalOrgMember[]> {
    const response = await this.makeRequest<ApiResponse<ExternalOrgMember[]>>(
      `${this.config.baseEndpoint}/host-orgs/${relationshipId}/members`,
      {
        method: 'POST',
        body: data,
      },
    )
    return response.data
  }

  /**
   * Revoke member access to a host org
   */
  async revokeMemberAccess(
    relationshipId: string,
    data: RevokeExternalMemberAccessDTO,
  ): Promise<{ revoked: number }> {
    const response = await this.makeRequest<ApiResponse<{ revoked: number }>>(
      `${this.config.baseEndpoint}/host-orgs/${relationshipId}/members`,
      {
        method: 'DELETE',
        body: data,
      },
    )
    return response.data
  }

  // =====================
  // Partner-Initiated Support Requests
  // =====================

  /**
   * Send a support request to a host org (partner org asks to manage/support them)
   */
  async createSupportRequest(
    data: CreateExternalOrgSupportRequestDTO,
  ): Promise<ExternalOrgInvite> {
    const response = await this.makeRequest<ApiResponse<ExternalOrgInvite>>(
      `${this.config.baseEndpoint}/support-requests`,
      {
        method: 'POST',
        body: data,
      },
    )
    return response.data
  }

  /**
   * Get all support requests sent by the current org (partner perspective)
   */
  async getSentSupportRequests(
    params?: ExternalOrgParams,
  ): Promise<ExternalOrgInvite[]> {
    const queryString = this.buildQueryParams(params ?? {})
    const response = await this.makeRequest<ApiResponse<ExternalOrgInvite[]>>(
      `${this.config.baseEndpoint}/support-requests/sent${queryString}`,
    )
    return response.data
  }

  /**
   * Get pending support requests addressed to the current user's email (host perspective)
   */
  async getIncomingSupportRequests(): Promise<ExternalOrgInvite[]> {
    const response = await this.makeRequest<ApiResponse<ExternalOrgInvite[]>>(
      `${this.config.baseEndpoint}/support-requests/incoming`,
    )
    return response.data
  }

  /**
   * Look up a support request by token (public endpoint for accept page)
   */
  async getSupportRequestByToken(
    token: string,
  ): Promise<SupportRequestTokenData> {
    return this.makeRequest<SupportRequestTokenData>(
      `${this.config.baseEndpoint}/support-requests/token/${token}`,
    )
  }

  /**
   * Accept a support request (host admin chooses permission set)
   */
  async acceptSupportRequest(
    data: AcceptExternalOrgSupportRequestDTO,
  ): Promise<AcceptInviteResult> {
    const response = await this.makeRequest<ApiResponse<AcceptInviteResult>>(
      `${this.config.baseEndpoint}/support-requests/accept`,
      {
        method: 'POST',
        body: data,
      },
    )
    return response.data
  }

  /**
   * Decline a support request
   */
  async declineSupportRequest(token: string): Promise<ExternalOrgInvite> {
    const response = await this.makeRequest<ApiResponse<ExternalOrgInvite>>(
      `${this.config.baseEndpoint}/support-requests/decline`,
      {
        method: 'POST',
        body: { token },
      },
    )
    return response.data
  }
}

// Export singleton instance
export const externalOrgApi = createSingletonApi(ExternalOrgApiService)

// Export static-style class for backward compatibility
export class ExternalOrgApi {
  static getInstance = () => externalOrgApi

  // Assignable members
  static getAssignableExternalMembers = () =>
    externalOrgApi.getAssignableExternalMembers()

  // Invite methods
  static sendInvite = (data: CreateExternalOrgInviteDTO) =>
    externalOrgApi.sendInvite(data)
  static getSentInvites = (params?: ExternalOrgParams) =>
    externalOrgApi.getSentInvites(params)
  static cancelInvite = (id: string) => externalOrgApi.cancelInvite(id)
  static resendInvite = (id: string) => externalOrgApi.resendInvite(id)
  static getReceivedInvites = () => externalOrgApi.getReceivedInvites()
  static getInviteByToken = (token: string) =>
    externalOrgApi.getInviteByToken(token)
  static acceptInvite = (data: AcceptExternalOrgInviteDTO) =>
    externalOrgApi.acceptInvite(data)
  static declineInvite = (token: string) => externalOrgApi.declineInvite(token)

  // Partner methods (host perspective)
  static getPartnerOrgs = (params?: ExternalOrgParams) =>
    externalOrgApi.getPartnerOrgs(params)
  static getPartnerById = (id: string) => externalOrgApi.getPartnerById(id)
  static updatePartner = (id: string, data: UpdateExternalOrgRelationshipDTO) =>
    externalOrgApi.updatePartner(id, data)
  static suspendPartner = (id: string) => externalOrgApi.suspendPartner(id)
  static reactivatePartner = (id: string) =>
    externalOrgApi.reactivatePartner(id)
  static revokePartner = (id: string) => externalOrgApi.revokePartner(id)
  static getPartnerMembers = (id: string, params?: ExternalOrgParams) =>
    externalOrgApi.getPartnerMembers(id, params)

  // Host org methods (partner perspective)
  static getHostOrgs = (params?: ExternalOrgParams) =>
    externalOrgApi.getHostOrgs(params)
  static getMyHostOrgs = () => externalOrgApi.getMyHostOrgs()
  static getHostOrgMembers = (id: string, params?: ExternalOrgParams) =>
    externalOrgApi.getHostOrgMembers(id, params)
  static grantMemberAccess = (id: string, data: GrantExternalMemberAccessDTO) =>
    externalOrgApi.grantMemberAccess(id, data)
  static revokeMemberAccess = (
    id: string,
    data: RevokeExternalMemberAccessDTO,
  ) => externalOrgApi.revokeMemberAccess(id, data)

  // Support request methods
  static createSupportRequest = (data: CreateExternalOrgSupportRequestDTO) =>
    externalOrgApi.createSupportRequest(data)
  static getSentSupportRequests = (params?: ExternalOrgParams) =>
    externalOrgApi.getSentSupportRequests(params)
  static getIncomingSupportRequests = () =>
    externalOrgApi.getIncomingSupportRequests()
  static getSupportRequestByToken = (token: string) =>
    externalOrgApi.getSupportRequestByToken(token)
  static acceptSupportRequest = (data: AcceptExternalOrgSupportRequestDTO) =>
    externalOrgApi.acceptSupportRequest(data)
  static declineSupportRequest = (token: string) =>
    externalOrgApi.declineSupportRequest(token)
}

// Export types
export type {
  ExternalOrgApiService,
  ExternalOrgParams,
  InviteTokenData,
  AcceptInviteResult,
  SupportRequestTokenData,
}
